import {
  Avatar,
  ClickAwayListener,
  Divider,
  Grow,
  Hidden,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';
import ReloadFixturesMenuItem from 'components/Header/ReloadFixturesMenuItem';
import { AppContext } from 'context';
import { colors } from 'theme';
import { UserRole } from 'types';
import { Routes } from 'routes';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { useSortingCenterById } from 'hooks';
import CreateInitClosuresMenuItem from './Header/CreateInitClosuresMenuItem';

const useStyles = makeStyles(theme => ({
  wrapper: {
    padding: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'fixed',
    left: 0,
    right: 0,
    zIndex: theme.zIndex.drawer + 1, // Above the content
  },
  logo: {
    height: '2rem',
    marginLeft: '3.5rem',
  },
  user: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerDetails: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    backgroundColor: 'transparent',
    border: '1px solid',
    color: theme.palette.primary.main,
    width: '2rem',
    height: '2rem',
    marginLeft: '1rem',
    fontSize: '1rem',
  },
  userDetails: {
    marginLeft: '1rem',
    '& > *': {
      fontSize: '0.6875rem',
    },
  },
  name: {
    fontWeight: 700,
  },
  lastName: {
    textTransform: 'uppercase',
  },
  divider: {
    margin: '-1rem 0px -1rem 11px',
  },
  smallDivider: {
    marginLeft: '0.625rem',
    marginRight: '0.625rem',
    backgroundColor: colors.neutral800,
    width: '2px',
  },
  iconButton: {
    padding: '0',
    marginLeft: '2rem',
    color: 'black',
    width: '2rem',
    height: '2rem',
  },
  link: {
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    textDecoration: 'none',
    color: 'black',
  },
  support: {
    color: 'black',
    fontSize: '0.75rem',
    paddingLeft: '24px',
    paddingRight: '10px',
  },
  userMenu: {
    minWidth: '300px',
    textTransform: 'uppercase',
    color: colors.grey3,
    fontWeight: 600,
    fontSize: '14px',
  },
  userMenuItem: {
    paddingLeft: '36px',
  },
  menuZIndex: {
    zIndex: 1,
  },
}));

export const ROLE_HIERARCHY = [
  UserRole.ROLE_SUPER_ADMIN,
  UserRole.ROLE_SUPPORT,
  UserRole.ROLE_ADMIN,
  UserRole.ROLE_MANAGER,
  UserRole.ROLE_ASSISTANT,
  UserRole.ROLE_CONSULTATION,
];
const FIXTURES_RESET_ALLOWED_HOSTNAMES = /dev-etem|localhost/;

const Header: React.FC = () => {
  const context = useContext(AppContext);
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const { t } = useTranslation();

  const user = context.user;
  const sortingCenter = user ? context.getSortingCenter?.() : null;
  const sortingCenterId = transformObjectId(sortingCenter);
  const { value: sortingCenterFromHook } = useSortingCenterById(sortingCenterId ?? null);
  const roles = context.getRoles?.() ?? [];
  const userRole = user ? ROLE_HIERARCHY.find(r => roles.includes(r)) : null;
  const sortingCenters = context.getSortingCenters?.();
  const hasMultipleSortingCenters =
    sortingCenters &&
    (sortingCenters.length > 1 ||
      (sortingCenters.length === 1 &&
        sortingCenters[0].childrenSortingCenter &&
        sortingCenters[0].childrenSortingCenter.length > 1));

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleClickProfile = () => {
    history.push(Routes.UserProfile);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const displayResetFixturesButton =
    context.getRoles()?.includes(UserRole.ROLE_SUPER_ADMIN) &&
    FIXTURES_RESET_ALLOWED_HOSTNAMES.test(window.location.hostname);

  const isAdministrationLocation = location.pathname.startsWith('/administration');

  return (
    <Paper variant="outlined" className={classes.wrapper} square ref={anchorRef}>
      <Link to="/">
        <div className={classes.logo} />
      </Link>
      <div className={classes.headerDetails}>
        <Hidden smDown>
          <Link to="/politique-de-confidentialite.pdf" target="_blank" download className={classes.link}>
            {t('header.privacyPolicy')}
          </Link>
          <Divider className={classes.smallDivider} orientation="vertical" flexItem />
          <Link to="/cgu.pdf" target="_blank" download className={classes.link}>
            {t('header.termsOfUse')}
          </Link>
          <Divider className={classes.divider} orientation="vertical" flexItem />
          <Divider className={classes.divider} orientation="vertical" flexItem />
          <a
            href={`mailto:support.etem@citeo.com${
              sortingCenter ? `?subject=${encodeURIComponent(`Support pour ${sortingCenter.code}`)}` : ''
            }`}
            className={classes.support}
          >
            {t('header.support')}
          </a>
        </Hidden>
        {user && (
          <div className={classes.user}>
            <Hidden smDown>
              <Divider className={classes.divider} orientation="vertical" flexItem />
              <Divider className={classes.divider} orientation="vertical" flexItem />
              <Avatar className={classes.avatar}>
                {user.firstName ? user.firstName[0] : user.email[0]}
                {user.lastName ? user.lastName[0] : ''}
              </Avatar>
            </Hidden>
            <div className={classes.userDetails}>
              {sortingCenter && !isAdministrationLocation && (
                <Typography>{sortingCenterFromHook?.name ?? sortingCenter.name}</Typography>
              )}
              <Typography className={classes.name}>
                {user.firstName ? user.firstName + ' ' : user.email}
                <span className={classes.lastName}>{user.lastName ? user.lastName : ''}</span>
              </Typography>
              {isAdministrationLocation ? (
                <Typography>{t(`users.rolesEnum.ROLE_ADMIN`)}</Typography>
              ) : (
                userRole && <Typography>{t(`users.rolesEnum.${userRole}`)}</Typography>
              )}
            </div>
            <IconButton
              className={classes.iconButton}
              aria-label={open ? 'close' : 'open'}
              aria-controls={open ? 'menu-list-grow' : undefined}
              aria-haspopup="true"
              onClick={handleClick}
            >
              {open ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Popper
              className={classes.menuZIndex}
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              placement="bottom-end"
              modifiers={{
                preventOverflow: {
                  enabled: false,
                },
                hide: {
                  enabled: false,
                },
              }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: 'center top' }}>
                  <Paper className={classes.userMenu} square elevation={0} variant="outlined">
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList autoFocusItem={open} id="menu-list-grow">
                        {displayResetFixturesButton && <ReloadFixturesMenuItem />}
                        {displayResetFixturesButton && <CreateInitClosuresMenuItem />}
                        <MenuItem className={classes.userMenuItem} onClick={handleClickProfile}>
                          {t('header.profile')}
                        </MenuItem>
                        {(hasMultipleSortingCenters || context.hasLeastOneAdminRole()) && (
                          <MenuItem
                            className={classes.userMenuItem}
                            onClick={() => history.push(Routes.SortingCenterSelection)}
                          >
                            {t('header.changeSortingCenter')}
                          </MenuItem>
                        )}
                        <MenuItem className={classes.userMenuItem} onClick={context.logout}>
                          {t('header.logout')}
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>
        )}
      </div>
    </Paper>
  );
};
export default React.memo(Header);
